import { useViewport, ViewportPortal } from "@xyflow/react";
import { CANVAS_FLOATING_TOOLBAR_Z_INDEX } from "../../constants/canvasFloatingToolbar.constants";

interface CanvasFloatingToolbarPortalProps {
  positionAbsoluteX: number;
  positionAbsoluteY: number;
  width: number;
  /**
   * Upward offset in screen px — the constant screen-space gap block above
   * the node top: bridge (8) + toolbar (~42) + gap. E.g. 58 leaves an 8px
   * gap; chrome-wearing nodes add the band clearance while it's mounted.
   */
  topOffset?: number;
  onPointerDown?: (e: React.PointerEvent) => void;
  children: React.ReactNode;
}

/**
 * Shared ViewportPortal wrapper for all canvas floating toolbars.
 *
 * Handles:
 *   - Zoom-invariant sizing via scale(1/zoom)
 *   - Correct absolute flow-space positioning
 *   - Minimal pointer-event bridge (h-2) so popover interactions don't
 *     accidentally deselect the node
 *   - No hover callbacks needed — toolbar visibility is select-gated
 */
export function CanvasFloatingToolbarPortal({
  positionAbsoluteX,
  positionAbsoluteY,
  width,
  topOffset = 64,
  onPointerDown,
  children,
}: CanvasFloatingToolbarPortalProps) {
  const viewport = useViewport();

  return (
    <ViewportPortal>
      <div
        className="canvas-floating-toolbar-portal"
        style={{
          position: "absolute",
          left: positionAbsoluteX + width / 2,
          top: positionAbsoluteY - topOffset / viewport.zoom,
          transform: `translateX(-50%) scale(${1 / viewport.zoom})`,
          transformOrigin: "top center",
          pointerEvents: "auto",
          zIndex: CANVAS_FLOATING_TOOLBAR_Z_INDEX,
        }}
        onPointerDown={(e) => {
          // Stop propagation so React Flow doesn't process this as a canvas
          // click and deselect the node.
          e.stopPropagation();
          onPointerDown?.(e);
        }}
      >
        {/* Minimal bridge above — fills the visible gap between toolbar bottom
            and node (title pill / body). Pointer-events are captured here so
            popovers can be interacted with without losing selection. */}
        <div className="h-2 w-full" />
        {children}
        {/* Bridge below keeps pointer-events within the portal area */}
        <div className="h-2 w-full" />
      </div>
    </ViewportPortal>
  );
}
